import lodash from 'lodash'
import PropTypes from 'prop-types'
import * as React from 'react'

import {createOrCallElement} from './cloneOrCreate'

const isThemeable = Symbol('isThemeable')

export type ThemeableComponentProps = {
  theme?: Record<string, string>
}

export type ThemeableProps = {
  theme: Record<string, string>
}

export function overrideTheme(base?: any | null, theme?: any | null): any {
  if (!base && !theme) return {}
  if (!base) return theme || {}
  if (!theme) return base
  return {...base, ...theme}
}

// numeric values or anything with special characters
const LOCAL_CLASSNAME_REGEXP = /^.+--.+--.+$/

export function isLocalClassName(className: string) {
  return LOCAL_CLASSNAME_REGEXP.test(className)
}

export type Theme = any

export function composeTheme(base?: Theme | null, theme?: Theme | null): Theme {
  if (!base && !theme) return {}
  if (!base) return theme || {}
  if (!theme) return base
  return lodash.mergeWith<Theme, Theme, Theme>(
    {},
    base,
    theme,
    (objValue, srcValue) => {
      if (!objValue) return srcValue
      if (!srcValue) return objValue

      const srcIsClassName = isLocalClassName(srcValue)
      const objIsClassName = isLocalClassName(objValue)
      if (srcIsClassName && objIsClassName) {
        return objValue + ' ' + srcValue
      } else if (srcIsClassName === objIsClassName) {
        return srcValue
      } else if (objIsClassName) {
        throw new Error(
          `You're composing a value (${objValue}) with a class (${srcValue})`
        )
      }
      throw new Error(
        `You're composing a class (${objValue}) with a value (${srcValue})`
      )
    }
  )
}

export function applyTheme(base?: any | null, theme?: any | null): any {
  if (!base && !theme) return {}
  if (!base) return theme || {}
  if (!theme) return base

  const {themeCompose = 'all', themeBase} = theme
  if (themeBase === 'none') {
    return theme
  }
  if (themeCompose === 'all') {
    return composeTheme(base, theme)
  } else if (themeCompose === 'none') {
    return overrideTheme(base, theme)
  }

  if (TESTING) {
    return base
  }

  throw new Error(`Unhandled themeCompose: ${themeCompose}`)
}

// TODO this decorator SERIOUSLY needs tests - it's our theming basis!

/* eslint-disable no-param-reassign */
const themeable = (baseTheme?: any | null) => (Parent: any) => {
  // themeable may be applied multiple times
  if (!Parent[isThemeable]) {
    // mixin statics
    Parent.propTypes = {
      theme: PropTypes.object,
      ...(Parent.propTypes || {}),
    }

    // mixin the rest
    Object.defineProperties(Parent.prototype, {
      theme: {
        configurable: true,
        get: function themeGetter() {
          if (!this.__theme || this.props.theme !== this.__themeProp) {
            this.__themeProp = this.props.theme
            this.__theme = applyTheme(
              this.constructor.baseTheme,
              this.__themeProp
            )
          }
          return this.__theme
        },
      },
    })
  }

  Parent.baseTheme = applyTheme(Parent.baseTheme, baseTheme)

  Parent[isThemeable] = true
  return Parent
}

type WithTheme = <P>(
  Component: React.ComponentType<P>
) => React.ComponentType<P & ThemeableComponentProps>

// Adds `theme` prop with `baseTheme`.
// If component has `theme` prop, it will be applied to baseTheme
export const withTheme =
  (baseTheme?: any | null): WithTheme =>
  (Component) => {
    return (props) =>
      createOrCallElement(Component, {
        ...props,
        theme:
          'theme' in props ? applyTheme(baseTheme, props.theme) : baseTheme,
      })
  }

export default themeable
